import { enUS, lv, ru } from "date-fns/locale";
import { format } from "date-fns";

export const getLocale = (locale: string) => {
  switch (locale) {
    case "en":
      return enUS;
    case "ru":
      return ru;
    case "lv":
    default:
      return lv;
  }
};

const lvMonthsDative = [
  "janvārīm",
  "februārīm",
  "martam",
  "aprīlim",
  "maijam",
  "jūnijam",
  "jūlijam",
  "augustam",
  "septembrim",
  "oktobrim",
  "novembrim",
  "decembrim",
];

const lvMonthsDativeWithS = [
  "janvārīs",
  "februārīs",
  "martīs",
  "aprīlīs",
  "maijīs",
  "jūnijīs",
  "jūlijīs",
  "augustīs",
  "septembrīs",
  "oktobrīs",
  "novembrīs",
  "decembrīs",
];

export const getMonthFormatDate = (date: string, locale: string) => {
  return format(new Date(date), "MMMM", { locale: getLocale(locale) });
};

export const getMonthWithDayFormatDate = (date: string, locale: string) => {
  const dateLocale = getLocale(locale);

  if (locale === "lv") {
    const monthIndex = new Date(date).getMonth();

    const day = new Date(date).getDate();
    return `${day}. ${lvMonthsDativeWithS[monthIndex]}`;
  }

  switch (dateLocale) {
    case ru:
      return format(new Date(date), "d MMMM", { locale: dateLocale });
    case lv:
      return format(new Date(date), "d. MMMM", { locale: dateLocale });
    default:
      return format(new Date(date), "MMMM d", { locale: getLocale(locale) });
  }
};

export const getShortMonthWithDayFormatDate = (
  date: string,
  locale: string
) => {
  const dateLocale = getLocale(locale);

  if (locale === "lv") {
    const monthIndex = new Date(date).getMonth();

    const day = new Date(date).getDate();
    return `${day}. ${lvMonthsDative[monthIndex]}`;
  }

  switch (dateLocale) {
    case ru:
      return format(new Date(date), "MMM d", { locale: dateLocale });
    case lv:
      return format(new Date(date), "d. MMMM", { locale: dateLocale });
    default:
      return format(new Date(date), "MMM'.' d", { locale: dateLocale });
  }
};

export const getShortMonthWithYearFormatDate = (
  date: string,
  locale: string
) => {
  const dateLocale = getLocale(locale);
  if (dateLocale === enUS) {
    return format(new Date(date), "MMM'.' yyyy", { locale: dateLocale });
  }

  return format(new Date(date), "MMM yyyy", { locale: dateLocale });
};

export const getFormattedDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const getFormattedTime = (date: Date): string => {
  const hour = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");
  return `${hour}:${minutes}`;
};

export const getFormattedDateTime = (date: Date): string => {
  return getFormattedDate(date) + " " + getFormattedTime(date);
};

export const datesEquals = (date: Date, other: Date) => {
  return (
    date.getFullYear() === other.getFullYear() &&
    date.getMonth() === other.getMonth() &&
    date.getDate() === other.getDate()
  );
};

export const getMonthYearFormat = (date: string, locale: string): string => {
  const [year, month] = date.split("-");
  const formattedDate = new Date(Number(year), Number(month) - 1);
  return format(formattedDate, "MMMM yyyy", { locale: getLocale(locale) });
};

const isToday = (date: Date) => {
  const now = new Date();
  return (
    now.getFullYear() === date.getFullYear() &&
    now.getMonth() === date.getMonth() &&
    now.getDate() === date.getDate()
  );
};

const isYesterday = (date: Date) => {
  const now = new Date();
  return (
    now.getFullYear() === date.getFullYear() &&
    now.getMonth() === date.getMonth() &&
    now.getDate() - 1 === date.getDate()
  );
};

export const getFormatChatDate = (
  date: Date,
  locale: string,
  t: (key: string) => string
): string => {
  const now = new Date();
  if (now.getTime() - date.getTime() < 60 * 1000) {
    return t("just_now");
  } else if (isToday(date)) {
    return `${t("today_at")} ${getFormattedTime(date)}`;
  } else if (isYesterday(date)) {
    return `${t("yesterday_at")} ${getFormattedTime(date)}`;
  }

  const dateLocale = getLocale(locale);
  if (dateLocale === enUS) {
    return format(new Date(date), "MMM'.' d, yyyy", { locale: dateLocale });
  }

  return format(new Date(date), "MMM d, yyyy", { locale: dateLocale });
};

export const getMonthLocale = (monthIndex: number, locale: string) => {
  const date = new Date();
  date.setMonth(monthIndex);
  return date.toLocaleDateString(locale, { month: "long" });
};
